import update from 'immutability-helper';
import { FC, useEffect, useReducer, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { Section } from 'src/components/widgets/section';
import { SectionContent } from 'src/components/widgets/section/sectionContent';
import { SectionDescription } from 'src/components/widgets/section/sectionDescription';
import { SectionHeader } from 'src/components/widgets/section/sectionHeader';
import Stack from 'src/components/widgets/stack/stack';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Button } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../widgets/headers';
import { useProductConfigurationState } from '../../state';
import SelectedOptionValue from '../../state/selectedOptionValue';
import { WizardFooterBackButton } from '../wizardFooterBackButton';
import { variantsQuery$data } from './__generated__/variantsQuery.graphql';
import { MerchantProductVariantImageMatch } from './helpers';

const AttributeBlock = styled.div`
    margin-bottom: 28px;
`;

const AttributeHeader = styled.div`
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eceff1;
`;

const ValuesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px 16px;
`;

const CompactSection = styled(Section)`
    ${SectionHeader} {
        padding-bottom: 0;
    }
    ${SectionDescription} {
        margin-bottom: 0;
    }
    ${SectionContent} {
        padding-top: 8px;
    }
`;

const AttributeLabel = styled.h4`
    margin: 0;
    font-size: 15px;
`;

interface SelectAction {
    type: 'SELECT';
    optionId: string;
    valuesIds: string[];
}

interface DeselectAction {
    type: 'DESELECT';
    optionId: string;
    valuesIds: string[];
}

type ActionType = SelectAction | DeselectAction;

// State is a map where key is the option and value is a set of selected values
const OptionsListReducer = (prevState: Map<string, Set<string>>, action: ActionType) => {
    // Initialized the list of selected values for this option if not exist
    let optionValues = prevState.get(action.optionId)!;
    if (!optionValues) {
        optionValues = new Set<string>();
        prevState = update(prevState, { $add: [[action.optionId, optionValues]] });
    }

    switch (action.type) {
        case 'SELECT':
            optionValues = update(optionValues, { $add: action.valuesIds.filter((x) => !optionValues.has(x)) });
            break;
        case 'DESELECT':
            optionValues = update(optionValues, { $remove: action.valuesIds });
            break;
    }

    prevState = update(prevState, { [action.optionId]: { $set: optionValues } });
    return prevState;
};

interface OptionsListProps {
    onAddVariantClick: (
        selectedOptionsValues: SelectedOptionValue[],
        merchantProductVariantImageMatches: MerchantProductVariantImageMatch[] | null
    ) => void | Promise<void>;
    onCancelClick: () => void;
    options: variantsQuery$data['merchantProductOptions'];
}

const OptionsList: FC<OptionsListProps> = ({ onAddVariantClick, onCancelClick, options }) => {
    const [allSelectedValues, dispatch] = useReducer(OptionsListReducer, new Map<string, Set<string>>());
    const { showDialog, closeDialog } = useDialogManager();

    const [productVariantImageMatches, setProductVariantImageMatches] = useState<MerchantProductVariantImageMatch[] | null>(null);
    const [productVariantImageMatchesInFlight, setproductVariantImageMatchesInFlight] = useState(false);
    const [pendingAddVariantAction, setPendingAddVariantAction] = useState<SelectedOptionValue[] | null>(null);
    const [isAddingVariants, setIsAddingVariants] = useState(false);

    type OptionType = OptionsListProps['options'][number];

    const isOptionValueSelected = (optionId: string, valueId: string) => {
        const optionValues = allSelectedValues.get(optionId);
        return (optionValues && optionValues.has(valueId)) || false;
    };

    const isAllSelected = (option: OptionType) => {
        const selectedOptionValues = allSelectedValues.get(option.optionID);
        return (selectedOptionValues && !option.values.some((x) => !selectedOptionValues.has(x.valueID))) || false;
    };

    // Whether every attribute is fully selected (used to decide whether to wait for store image matching).
    const isEverySelected = () => options.length > 0 && options.every((o) => isAllSelected(o));

    const handleSelectAllChange = (option: OptionType) => {
        dispatch({
            type: isAllSelected(option) ? 'DESELECT' : 'SELECT',
            optionId: option.optionID,
            valuesIds: option.values.map((x) => x.valueID) ?? []
        });
    };

    const handleOptionChange = (option: OptionType, value: { valueID: string }) => {
        dispatch({
            type: isOptionValueSelected(option.optionID, value.valueID) ? 'DESELECT' : 'SELECT',
            optionId: option.optionID,
            valuesIds: [value.valueID]
        });
    };
    const handleAddVariantClick = async () => {
        const selectedOptionsValues: SelectedOptionValue[] = [];

        options.forEach((option) => {
            option.values.forEach((value) => {
                if (isOptionValueSelected(option.optionID, value.valueID)) {
                    selectedOptionsValues.push({
                        Id: option.optionID,
                        Label: option.label,
                        Value: {
                            Id: value.valueID,
                            Label: value.label
                        }
                    });
                }
            });
        });

        if (productVariantImageMatches === null && productVariantImageMatchesInFlight && isEverySelected()) {
            setPendingAddVariantAction(selectedOptionsValues);
            showDialog(
                'waitForImagesDialog',
                <QuestionDialog
                    title={T._('Image Matching In Progress', 'Admin')}
                    message={T._(
                        'We are matching images to your product variants. This will proceed automatically once complete. You can cancel this process.',
                        'Admin'
                    )}
                    buttonNoLabel={T._('Cancel and continue without getting the images of the variants from the store', 'Admin')}
                    buttonYesLabel={T._('Wait for images', 'Admin')}
                    onYesClick={() => {
                        closeDialog('waitForImagesDialog');
                    }}
                    onNoClick={() => {
                        setPendingAddVariantAction(null);
                        setproductVariantImageMatchesInFlight(false);
                        closeDialog('waitForImagesDialog');
                    }}
                />
            );
        } else {
            setIsAddingVariants(true);
            try {
                await onAddVariantClick(selectedOptionsValues, productVariantImageMatches);
            } finally {
                setIsAddingVariants(false);
            }
        }
    };

    const { state } = useProductConfigurationState();
    const relayEnvironment = useRelayEnvironment();
    useEffect(() => {
        if (pendingAddVariantAction && !productVariantImageMatchesInFlight) {
            setIsAddingVariants(true);
            Promise.resolve(onAddVariantClick(pendingAddVariantAction, productVariantImageMatches)).finally(() => setIsAddingVariants(false));
            closeDialog('waitForImagesDialog');
            setPendingAddVariantAction(null);
        }
    }, [pendingAddVariantAction, productVariantImageMatchesInFlight, productVariantImageMatches, onAddVariantClick, closeDialog, setPendingAddVariantAction]);
    useEffect(() => {
        setproductVariantImageMatchesInFlight(true);
        fetchQuery(
            relayEnvironment,
            graphql`
                query addVariantOptionsListQuery($sku: String!, $salesChannelID: ID!) {
                    productVariantImageMatches(sku: $sku, salesChannelID: $salesChannelID) {
                        imageUrl
                        selections {
                            optionID
                            label
                            value {
                                id
                                label
                            }
                        }
                    }
                }
            `,
            { sku: state.product.code || '', salesChannelID: state.salesChannelID }
        )
            .toPromise()
            .then((data) => {
                const typedData = data as { productVariantImageMatches?: MerchantProductVariantImageMatch[] };
                setProductVariantImageMatches(typedData.productVariantImageMatches || []);
                setproductVariantImageMatchesInFlight(false);
            })
            .catch((error) => {
                console.error('Error fetching product variant image matches:', error);
                setproductVariantImageMatchesInFlight(false);
            });
    }, [relayEnvironment, state.product.code, state.salesChannelID]);

    return (
        <>
            <CompactSection
                description={
                    <>
                        <strong>Select the attributes you want to import</strong>
                    </>
                }
            >
                {options.map((option) => (
                    <AttributeBlock key={option.optionID}>
                        <AttributeHeader>
                            <AttributeLabel>{option.label}</AttributeLabel>
                        </AttributeHeader>
                        <Stack $gap='12px'>
                            <Checkbox checked={isAllSelected(option)} onChange={() => handleSelectAllChange(option)} label={T._('Select all', 'Admin')} />
                            <ValuesGrid>
                                {option.values.map((value, idx) => (
                                    <Checkbox
                                        key={value.valueID + '-' + idx}
                                        checked={isOptionValueSelected(option.optionID, value.valueID)}
                                        onChange={() => handleOptionChange(option, value)}
                                        label={value.label}
                                    />
                                ))}
                            </ValuesGrid>
                        </Stack>
                    </AttributeBlock>
                ))}
            </CompactSection>
            <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={onCancelClick} />}>
                <Button onClick={handleAddVariantClick} disabled={isAddingVariants}>
                    {isAddingVariants ? T._('Adding variants...', 'Admin') : T._('Add variants', 'Admin')}
                </Button>
            </PageFooterButtons>
        </>
    );
};

export default OptionsList;
